import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BG_IMAGE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_c8c33088-e275-4c7a-be4e-9570c082ca94/artifacts/z3bd0i09_W40.jpg";

const PROJECT_TYPES = [
  "French Doors",
  "Awning Windows",
  "Arched",
  "Bifold",
  "Custom Steel Doors",
  "Pivot Doors",
  "Casement Windows",
  "Sliding",
  "Other",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  project: PROJECT_TYPES[0],
  message: "",
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const emailAddr = form.email.trim();
    if (!emailAddr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddr)) {
      setError("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      // Route general enquiries through the same /api/quotes pipeline so they
      // hit Resend → orders@euro-steel.co.nz with a neatly-formatted internal
      // email (name/phone/project-type as spec_summary rows, message as notes).
      await axios.post(`${BACKEND}/api/quotes`, {
        product_code: "ENQUIRY",
        product_name: `General Enquiry — ${form.project}`,
        email: emailAddr,
        phone: form.phone.trim() || null,
        notes: form.message.trim() || null,
        spec_summary: [
          { label: "Name", value: form.name.trim() || "—" },
          { label: "Project Type", value: form.project },
          ...(form.phone.trim()
            ? [{ label: "Phone", value: form.phone.trim() }]
            : []),
        ],
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Sorry — we couldn't send your enquiry right now. Please try again or email orders@euro-steel.co.nz directly.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="enquire"
      data-testid="enquiry-form-section"
      className="relative z-10 min-h-screen w-full overflow-hidden px-6 py-24 lg:px-16 lg:py-32"
    >
      {/* Full-bleed background image */}
      <img
        src={BG_IMAGE}
        alt="Loft interior with steel-framed industrial windows"
        data-testid="enquiry-form-background"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Overlay for readability */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/55 to-black/70"
      />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        {/* Left — heading */}
        <div className="lg:col-span-5">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            data-testid="enquiry-form-eyebrow"
            className="font-tech text-[10px] uppercase tracking-[0.4em] text-[#c6a87c]"
          >
            — Get in touch
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            data-testid="enquiry-form-heading"
            className="mt-5 font-serif-display text-5xl font-light leading-[1.02] tracking-[-0.02em] text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)] lg:text-6xl"
          >
            Start your <span className="text-[#c6a87c]">enquiry.</span>
          </motion.h2>

          <p className="mt-6 max-w-md font-body text-[15px] leading-[1.7] text-white/80 drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">
            Share your project details and we&apos;ll come back with pricing,
            lead-times and drawings tailored to your build. Prefer to call?{" "}
            <a
              href="tel:+64279152526"
              className="text-[#c6a87c] underline-offset-4 hover:underline"
            >
              +64 27 915 2526
            </a>
            .
          </p>
        </div>

        {/* Right — form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
          data-testid="enquiry-form"
          onSubmit={handleSubmit}
          className="rounded-sm border border-white/15 bg-black/45 p-6 backdrop-blur-md lg:col-span-7 lg:p-10"
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field
              testid="enquiry-name"
              label="Name"
              type="text"
              required
              value={form.name}
              onChange={update("name")}
            />
            <Field
              testid="enquiry-email"
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
            />
            <Field
              testid="enquiry-phone"
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={update("phone")}
            />
            <div className="flex flex-col gap-2">
              <label className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/60">
                Project Type
              </label>
              <select
                data-testid="enquiry-project"
                value={form.project}
                onChange={update("project")}
                className="border-b border-white/25 bg-transparent py-3 font-body text-white outline-none transition-colors duration-300 focus:border-[#c6a87c]"
              >
                {PROJECT_TYPES.map((p) => (
                  <option key={p} value={p} className="bg-[#0a0a0c]">
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <label className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/60">
              Message
            </label>
            <textarea
              data-testid="enquiry-message"
              rows={5}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about your project, dimensions, timeline..."
              className="resize-none border-b border-white/25 bg-transparent py-3 font-body text-white placeholder:text-white/35 outline-none transition-colors duration-300 focus:border-[#c6a87c]"
            />
          </div>

          <button
            type="submit"
            disabled={sending || sent}
            data-testid="enquiry-submit"
            className="mt-8 inline-block rounded-full bg-[#c6a87c] px-10 py-4 font-tech text-[11px] uppercase tracking-[0.3em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93] disabled:cursor-wait disabled:opacity-60"
          >
            {sent ? "Enquiry Sent ✓" : sending ? "Sending…" : "Send Enquiry"}
          </button>

          {error && (
            <p
              data-testid="enquiry-error"
              className="mt-5 font-tech text-[11px] uppercase tracking-[0.26em] text-red-300"
            >
              {error}
            </p>
          )}

          {sent && (
            <p
              data-testid="enquiry-sent-note"
              className="mt-5 font-tech text-[11px] uppercase tracking-[0.3em] text-[#c6a87c]"
            >
              Thanks — your enquiry is on its way to our team. We'll be in touch
              within one business day.
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ testid, label, type, required, value, onChange }) => (
  <div className="flex flex-col gap-2">
    <label className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/60">
      {label}
      {required && <span className="ml-1 text-[#c6a87c]">*</span>}
    </label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      className="border-b border-white/25 bg-transparent py-3 font-body text-white placeholder:text-white/35 outline-none transition-colors duration-300 focus:border-[#c6a87c]"
    />
  </div>
);
